// Keep the offline cache honest after a WRITE.
//
// ── The bug this exists for ────────────────────────────────────────────────
//
// A customer renames a device, a room or a button on the phone. The write goes
// to the server and succeeds. The name does not change — not on the next screen,
// not after navigating back, **not after restarting the app**. It looks exactly
// like "rename is not working", and the row in the database says otherwise.
//
// Nothing was wrong with the write. The mobile shell reads local-first
// (api/localFirst.js): when the IndexedDB cache holds rows it serves them and
// never asks the server. And the cache was WRITE-ONLY at sync time —
// `db/localRepo.js` is all reads, and the only refresh is `runSyncIfNeeded`,
// which runs at launch and only when the server has flagged the user's row
// `SinkData = 1`. Nothing in the app has ever set that flag; the app only
// CLEARS it. So a locally-made rename could sit invisible indefinitely, until
// the Me tab's manual sync or some unrelated server-side change.
//
// ⚠️ THIS IS A MOBILE-ONLY FAULT, which is why it arrives as a customer report
// and never reproduces at a desk. `dbSupported()` is
// `isNativePlatform() && indexedDB`, so the desktop shell has no cache, always
// reads REST, and renames there have always looked fine.
//
// ── Why the patch lives at the API layer ───────────────────────────────────
//
// These are called from the rename helpers in core/api/api.js and from
// core/devices/buttonSettings, NOT from the screens — so every entry point gets
// it and none can forget. That matters here: the same feature already shipped
// broken five times over because each caller had to remember to refresh itself.
//
// Every function is best-effort and never throws. A cache that fails to patch
// is stale, which is what it already was; a cache patch that broke the rename
// would be worse than the bug.

import { dbSupported } from './schema';
import {
  getAll, getAllByIndex, putOne, replaceAll,
} from './idb';

const best = async (fn) => {
  if (!dbSupported()) return;
  try {
    await fn();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[LocalPatch]', (e && e.message) || e);
  }
};

/*
 * ⚠️ BOTH THE FLAT COLUMN AND `raw` — a record is
 * `{ <key columns>, raw: <untouched server row> }`, and the two are read by
 * different paths. `localRepo.getRooms` returns `rawOf(rec)`, so it needs
 * `raw.RoomName`; `localRepo.enrich` stamps a device's RoomName from the FLAT
 * `room_name`. Patch one and the room list disagrees with the device list.
 */
const merged = (rec, flat, rawKey, value) => ({
  ...rec,
  ...(flat ? { [flat]: value } : {}),
  raw: { ...(rec.raw || {}), [rawKey]: value },
});

/**
 * After a favourite is added or removed: the cached favourites list, replaced
 * with the server's fresh one (the same record shape fullSync writes). Before
 * this, a star tapped on a phone changed the server and nothing on screen —
 * the list is read local-first and the cache never heard of the change.
 */
export const replaceCachedFavourites = (serverRows) => best(async () => {
  const n = (v) => (v == null || v === '' ? null : Number(v));
  const records = (serverRows || [])
    .filter((r) => r && r.UniqueNumber != null && String(r.UniqueNumber) !== '')
    .map((r) => ({
      unique_number: String(r.UniqueNumber),
      device_installed_id: n(r.DeviceInstalledID ?? r.DeviceInstalledId ?? r.ID ?? r.Id),
      raw: r,
    }));
  await replaceAll([{ store: 'favourite_devices', records }]);
});

/** After renaming a device: `DevicesInstalled.DeviceName`. */
export const patchCachedDeviceName = (uniqueNumber, deviceName) => best(async () => {
  const recs = await getAllByIndex('devices_installed', 'by_uno', String(uniqueNumber));
  await Promise.all((recs || []).map(
    (rec) => putOne('devices_installed', merged(rec, 'device_name', 'DeviceName', deviceName)),
  ));
});

/** After renaming one button: `OutputDetails.OutputName` for that output. */
export const patchCachedOutputName = (uniqueNumber, output, outputName) => best(async () => {
  const recs = await getAllByIndex('output_details', 'by_uno', String(uniqueNumber));
  const want = Number(output);
  await Promise.all((recs || [])
    .filter((rec) => Number(rec.output_index) === want)
    .map((rec) => putOne('output_details', merged(rec, 'output_name', 'OutputName', outputName))));
});

/**
 * After renaming a room: `Rooms.RoomName`.
 *
 * The store is keyed on `room_id` and has no index, so this reads the rooms
 * table whole — it is the smallest of them (one row per room the user can see).
 */
export const patchCachedRoomName = (roomId, roomName) => best(async () => {
  const recs = await getAll('rooms');
  const want = Number(roomId);
  await Promise.all((recs || [])
    .filter((rec) => Number(rec.room_id) === want)
    .map((rec) => putOne('rooms', merged(rec, 'room_name', 'RoomName', roomName))));
});
